/* The For you feed in the client's own words (R3-U12). The server stores
   one title per event for every reader ("A request item was answered");
   this turns the kinds the client sees into plain second-person lines, and
   falls back to the stored title for any other kind. Plain logic, so each
   line can be tested without a browser. */

type TimelineItem = {
  eventType: string
  actor: { kind: "firm_user" | "portal_contact" | "system"; displayName?: string }
  display: { title: string; detail?: string }
}

/** The event's detail. Older request events stored the first day of a
    period ("2026-08-01") there; a request is not named by a month, so that
    detail is left off. */
const readableDetail = (detail: string | undefined): string | null => {
  const value = detail?.trim()
  if (!value) return null
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? null : value
}

const withDetail = (line: string, detail: string | null) =>
  detail ? `${line}: ${detail}` : line

/**
 * One feed line. `me` is the signed-in contact's name: an event another
 * contact at the client made keeps their name, one of the client's own
 * reads "You".
 */
export const activityLine = (
  item: TimelineItem,
  firmName: string,
  me: string
): string => {
  const detail = readableDetail(item.display.detail)
  const byFirm = item.actor.kind === "firm_user"
  const byMe =
    item.actor.kind === "portal_contact" &&
    (!item.actor.displayName || item.actor.displayName === me)
  const who = byMe ? "You" : (item.actor.displayName ?? "Someone at your company")
  const title = item.display.title
  switch (item.eventType) {
    case "request_item_answered":
      return withDetail(`${who} answered`, detail)
    case "request_item_reopened":
      return byFirm
        ? withDetail(`${firmName} asked again`, detail)
        : withDetail(`${who} changed an answer`, detail)
    case "engagement_action_reported":
      return withDetail(`${who} finished a step`, detail)
    case "engagement_item_reopened":
      return withDetail(`${who} changed an answer`, detail)
    case "engagement_help_requested":
      return withDetail(`${who} asked ${firmName} for help`, detail)
    case "engagement_waiver_requested":
      return withDetail(`${who} asked to skip`, detail)
    case "client_file_deleted":
      return `${who} deleted a file`
    case "engagement_item_reject":
      return withDetail(`${firmName} asked again`, detail)
    case "request_item_accepted":
    case "engagement_item_verify":
      return withDetail(`${firmName} accepted`, detail)
    case "transaction_questions_asked":
      return `${firmName} asked you about some transactions`
    case "request_created":
      return withDetail(`${firmName} sent you a request`, detail)
    case "request_completed":
      return `${firmName} has everything it needs for a request`
    case "request_client_done":
      return `${who} told ${firmName} everything is sent`
    case "request_client_undone":
      return `${who} took back “I've sent everything”`
    case "file_matched":
      return `A file ${byMe ? "you" : who} sent went on your list`
    case "document_processed":
      return `${firmName} extracted a file you sent`
    case "engagement_started":
      return `${firmName} started working with you`
    case "delivery_published":
      return `${firmName} sent you finished work`
    case "delivery_acknowledged":
      return `${who} told ${firmName} the work came in`
    case "thread_message_sent": {
      const subject = title.replace(/^New message:\s*/, "").trim()
      return withDetail(
        byFirm ? `${firmName} sent you a message` : `${who} sent a message`,
        subject || null
      )
    }
    case "signature_completed": {
      const signed = title.match(/ signed (.+)$/)?.[1]
      return signed ? `${who} signed ${signed}` : title
    }
    case "deliverable_acknowledged": {
      const file = title.match(/ received (.+)$/)?.[1]
      return file ? `${who} told ${firmName} ${byMe ? "you have" : "they have"} ${file}` : title
    }
    default:
      return title
  }
}
